import type { KpiAxisConfiguration } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { getUniformBinSize } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';

export const KPI_AXIS_METHOD_NA = 'NA';

export type KpiAxisInteractionSource = 'input' | 'slider';
export type KpiAxisInteractionMethod = KpiAxisInteractionSource | 'both' | typeof KPI_AXIS_METHOD_NA;

type MethodFlags = { input: boolean; slider: boolean };

const emptyFlags = (): MethodFlags => ({ input: false, slider: false });

const sameBreakpoints = (left: number[], right: number[]) =>
    left.length === right.length && left.every((value, index) => value === right[index]);

export const resolveKpiAxisMethod = (flags: MethodFlags, didChange: boolean): KpiAxisInteractionMethod => {
    if (!didChange) return KPI_AXIS_METHOD_NA;
    if (flags.input && flags.slider) return 'both';
    if (flags.input) return 'input';
    if (flags.slider) return 'slider';
    return KPI_AXIS_METHOD_NA;
};

export const createKpiAxisMethodTracker = () => {
    const rangeFlags = emptyFlags();
    const breakpointFlags = emptyFlags();
    let lastMin = 0;
    let lastMax = 0;
    let lastBreakpoints: number[] = [];

    const reset = (config: KpiAxisConfiguration) => {
        rangeFlags.input = false;
        rangeFlags.slider = false;
        breakpointFlags.input = false;
        breakpointFlags.slider = false;
        lastMin = config.min;
        lastMax = config.max;
        lastBreakpoints = [...config.breakpoints];
    };

    const noteRange = (source: KpiAxisInteractionSource, min: number, max: number) => {
        if (min === lastMin && max === lastMax) return;
        lastMin = min;
        lastMax = max;
        rangeFlags[source] = true;
    };

    const noteBreakpoints = (source: KpiAxisInteractionSource, breakpoints: number[]) => {
        if (sameBreakpoints(breakpoints, lastBreakpoints)) return;
        lastBreakpoints = [...breakpoints];
        breakpointFlags[source] = true;
    };

    const getFlags = () => ({
        range: { ...rangeFlags },
        breakpoints: { ...breakpointFlags },
    });

    return { reset, noteRange, noteBreakpoints, getFlags };
};

export const buildKpiAxisConfigAppliedProperties = ({
    pageName,
    axisName,
    config,
    baseline,
    rangeFlags,
    breakpointFlags,
}: {
    pageName: string;
    axisName: string;
    config: KpiAxisConfiguration;
    baseline: KpiAxisConfiguration;
    rangeFlags: MethodFlags;
    breakpointFlags: MethodFlags;
}) => {
    const properties: Record<string, unknown> = {
        page_name: pageName,
        axis_name: axisName,
        previous_range_min: baseline.min,
        selected_range_min: config.min,
        previous_range_max: baseline.max,
        selected_range_max: config.max,
        range_method: resolveKpiAxisMethod(
            rangeFlags,
            baseline.min !== config.min || baseline.max !== config.max,
        ),
        binning_method: config.binningMethod,
    };

    if (config.binningMethod === 'uniform') {
        properties.previous_bin_count = baseline.binCount;
        properties.selected_bin_count = config.binCount;
        properties.previous_bin_size = getUniformBinSize(baseline.min, baseline.max, baseline.binCount);
        properties.selected_bin_size = getUniformBinSize(config.min, config.max, config.binCount);
        properties.custom_binning_method = KPI_AXIS_METHOD_NA;
        return properties;
    }

    properties.breakpoint_count = config.breakpoints.length;
    properties.breakpoints = config.breakpoints;
    properties.custom_binning_method = resolveKpiAxisMethod(
        breakpointFlags,
        !sameBreakpoints(baseline.breakpoints, config.breakpoints),
    );
    return properties;
};
